import { useState } from "react";
import { useTranslation } from "react-i18next";
import "./App.css";
import { Select } from "./Components/Select.tsx";

interface VoteDistributions {
  craziness: Record<number, number>;
  drinkAmount: Record<number, number>;
  sexualScaleVotes: number[];
}

interface AIRoomVoteProps {
  roomName: string;
  sessionId: string;
  socket: any;
  setAIRoomState: (state: string) => void;
  voteDistributions: VoteDistributions | null;
  myVote: {
    craziness: number;
    sexualScale: number;
    drinkAmount: number;
  } | null;
  showInfoMessage: (
    type: string,
    message: string,
    isAdd: boolean,
    duration?: string,
    isDelay?: boolean,
  ) => void;
}

function VoteDots({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <span style={{ marginLeft: 8, display: "inline-flex", gap: 3 }}>
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "black",
            opacity: 0.3,
            display: "inline-block",
          }}
        />
      ))}
    </span>
  );
}

function AIRoomVote({
  roomName,
  sessionId,
  socket,
  setAIRoomState,
  voteDistributions,
  myVote,
  showInfoMessage,
}: AIRoomVoteProps) {
  const { t } = useTranslation();

  const [craziness, setCraziness] = useState(myVote?.craziness ?? 0);
  const [sexualScale, setSexualScale] = useState(myVote?.sexualScale ?? 0);
  const [drinkAmount, setDrinkAmount] = useState(myVote?.drinkAmount ?? 0);
  const [step, setStep] = useState(0); // 0 = craziness+sexual, 1 = drink amount

  const submitVote = () => {
    if (craziness === 0) {
      showInfoMessage("warning", "Velg stemningsnivå", false);
      return;
    }
    if (drinkAmount === 0) {
      showInfoMessage("warning", "Velg drikkenivå", false);
      return;
    }
    socket.emit("ai room submit vote", {
      roomName,
      sessionId,
      craziness,
      sexualScale,
      drinkAmount,
    });
    showInfoMessage("", "Stemt!", true, "short");
    setAIRoomState("lobby");
  };

  const getDotCount = (
    category: "craziness" | "drinkAmount",
    value: number,
  ) => {
    if (!voteDistributions) return 0;
    return voteDistributions[category][value] || 0;
  };

  if (step === 0) {
    return (
      <div className="App-header">
        <div className="container-row lift-topbar">
          <div className="container-row-start">
            <div
              className="button-tertiary-m button-text-m text-color-black"
              onClick={() => setAIRoomState("lobby")}
              style={{ maxHeight: "32.5px" }}
            >
              {t("back")}
            </div>
          </div>
          <div className="text-h3 text-color-black left-margin-s text-force-break">
            {roomName}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <div style={{ marginTop: 32 }}>
            <p
              className="text-h2 text-color-black"
              style={{ marginBottom: 24 }}
            >
              STEMNINGSRAPPORT
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[
                { label: "ROLIG OPPVARMING", value: 1 },
                { label: "GODE I GASSEN", value: 2 },
                { label: "FYLLAAAAAAAAAA", value: 3 },
                { label: "CAN'T STAND", value: 4 },
              ].map((opt) => (
                <div
                  key={opt.value}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <Select
                    label={opt.label}
                    value={opt.value}
                    current={craziness}
                    onSelect={setCraziness}
                  />
                  <VoteDots count={getDotCount("craziness", opt.value)} />
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 64 }}>
            <p
              className="text-h2 text-color-black"
              style={{ marginBottom: 24 }}
            >
              SEKSUELL SKALA
            </p>
            <input
              style={{ padding: "0px 6px 0px 6px" }}
              className="square-slider"
              type="range"
              min={0}
              max={100}
              step={1}
              value={sexualScale}
              onChange={(e) => setSexualScale(Number(e.target.value))}
            />
            {voteDistributions &&
              voteDistributions.sexualScaleVotes.length > 0 && (
                <p
                  className="text-color-black"
                  style={{ opacity: 0.5, fontSize: 14, marginTop: 8 }}
                >
                  {voteDistributions.sexualScaleVotes.length} har stemt
                </p>
              )}
          </div>
        </div>

        <div
          className="button-primary black-fill button-text-l text-color-pink shadow-pink box-button-center"
          onClick={() => setStep(1)}
        >
          NESTE
        </div>
      </div>
    );
  }

  // Step 1: Drink amount
  return (
    <div className="App-header">
      <div className="container-row lift-topbar">
        <div className="container-row-start">
          <div
            className="button-tertiary-m button-text-m text-color-black"
            onClick={() => setStep(0)}
            style={{ maxHeight: "32.5px" }}
          >
            {t("back")}
          </div>
        </div>
        <div className="text-h3 text-color-black left-margin-s text-force-break">
          {roomName}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
        <div style={{ marginTop: 32 }}>
          <p
            className="text-h2 text-color-black"
            style={{ marginBottom: 24, lineHeight: 1.1 }}
          >
            HVOR MYE VIL DERE DRIKKE?
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { label: "ROLIG DRIKKING", value: 1 },
              { label: "VANLIG NORSK VORS", value: 2 },
              { label: "HARDE PÅ FLASKA", value: 3 },
              { label: "CHUGLIFE", value: 4 },
            ].map((opt) => (
              <div
                key={opt.value}
                style={{ display: "flex", alignItems: "center" }}
              >
                <Select
                  label={opt.label}
                  value={opt.value}
                  current={drinkAmount}
                  onSelect={setDrinkAmount}
                />
                <VoteDots count={getDotCount("drinkAmount", opt.value)} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div
        className="button-primary black-fill button-text-l text-color-pink shadow-pink box-button-center"
        onClick={submitVote}
      >
        STEM
      </div>
    </div>
  );
}

export default AIRoomVote;
